<script setup>
import Vapor from "laravel-vapor";
import WishBlockCard from "@/Components/Brand/sections/WishBlockCard.vue";
import BrandButton from "@/Components/Brand/BrandButton.vue";

// Desktop collage positions are percentages of the 1371x686 Figma frame.
const blocks = [
    {
        title: "Birthday Ideas for Emily's 7th.",
        subtitle: "Toys, games and stuffed animals",
        visibility: "Public",
        image: Vapor.asset("images/brand/wish-birthday.webp"),
        pos: "left-[5.2%] top-[68%]",
    },
    {
        title: "Camping Gear",
        subtitle: "Stuff I would love for camping this year!",
        visibility: "Public",
        image: Vapor.asset("images/brand/wish-camping.webp"),
        pos: "left-[37.8%] top-[77%]",
    },
    {
        title: "Gifts for My Wife",
        subtitle: "Hints dropped by Amy for our anniversary!",
        visibility: "Private",
        image: Vapor.asset("images/brand/wish-wife.webp"),
        pos: "left-[70.4%] top-[68%]",
    },
];

const addButtons = [
    "left-[10.4%] top-[55%]",
    "left-[47.7%] top-[67%]",
    "left-[86.7%] top-[55%]",
];
</script>

<template>
    <section class="px-5 py-6">
        <div
            class="mx-auto max-w-[1472px] overflow-hidden rounded-[10px] bg-[radial-gradient(86%_69%_at_53%_8%,#ffffff_40%,#e3dcda_100%)] px-5 py-16 lg:py-8"
        >
            <!-- Desktop: positioned collage stage -->
            <div
                class="relative mx-auto block aspect-[1371/720] w-full max-w-[1371px] max-lg:hidden xl:aspect-[1371/640]"
            >
                <!-- Header -->
                <p
                    class="absolute left-1/2 top-0 -translate-x-1/2 font-brand-display text-[18px] font-semibold uppercase italic text-brand-navy/40"
                >
                    Click. Send. Done.
                </p>
                <h2
                    class="absolute left-1/2 top-[3.5%] w-[70%] -translate-x-1/2 text-center font-brand-display font-black italic uppercase leading-[0.9] tracking-[-0.02em] text-brand-red text-[clamp(3rem,5.8vw,84px)]"
                >
                    Gifting Made Simple
                </h2>
                <p
                    class="absolute left-1/2 top-[18%] w-[60%] -translate-x-1/2 text-center xl:w-[45%] font-brand text-[18px] text-brand-navy"
                >
                    Anything you find online can be added in just a couple
                    clicks.
                </p>
                <BrandButton
                    :href="route('register')"
                    class="absolute left-1/2 top-[25%] -translate-x-1/2 px-8 py-3.5 text-[15px]"
                >
                    Start a Wish List
                </BrandButton>

                <!-- Products -->
                <img
                    loading="lazy"
                    :src="asset('images/brand/gifting-float-left.webp')"
                    alt=""
                    aria-hidden="true"
                    class="pointer-events-none absolute left-0 top-[24%] w-[26.6%]"
                />
                <img
                    loading="lazy"
                    :src="asset('images/brand/gifting-float-right.webp')"
                    alt=""
                    aria-hidden="true"
                    class="pointer-events-none absolute left-[75.7%] top-[14%] w-[24.3%]"
                />
                <img
                    loading="lazy"
                    :src="asset('images/brand/gifting-center.webp')"
                    alt="Gift Hero in action"
                    class="pointer-events-none absolute left-[30.9%] top-[39%] w-[36%]"
                />

                <!-- Decorative "+" markers (not controls; nothing to trigger) -->
                <img
                    v-for="(addPos, i) in addButtons"
                    :key="i"
                    loading="lazy"
                    :src="asset('images/brand/icon-add.svg')"
                    alt=""
                    aria-hidden="true"
                    class="pointer-events-none absolute z-10 size-[34px] select-none"
                    :class="addPos"
                />

                <!-- Wish-block cards -->
                <WishBlockCard
                    v-for="block in blocks"
                    :key="block.title"
                    :title="block.title"
                    :subtitle="block.subtitle"
                    :visibility="block.visibility"
                    :image="block.image"
                    class="absolute z-20 w-[26%] xl:w-[22.3%]"
                    :class="block.pos"
                />
            </div>

            <!-- Mobile / tablet: stacked -->
            <div class="lg:hidden">
                <div class="mx-auto max-w-[1400px] text-center">
                    <p
                        class="font-brand-display text-[24px] font-semibold uppercase italic text-brand-navy/40"
                    >
                        Click. Send. Done.
                    </p>
                    <h2
                        class="mt-2 font-brand-display font-black italic uppercase leading-[0.9] tracking-[-0.02em] text-brand-red text-[clamp(3.75rem,9vw,72px)]"
                    >
                        Gifting Made Simple
                    </h2>
                    <p
                        class="mx-auto mt-4 max-w-[480px] font-brand text-[17px] text-brand-navy"
                    >
                        Anything you find online can be added in just a couple
                        clicks.
                    </p>
                    <BrandButton
                        :href="route('register')"
                        class="mt-7 px-8 py-3.5 text-[15px]"
                    >
                        Start a Wish List
                    </BrandButton>
                </div>

                <!--
                    Mobile shows a single product with one overlapping wish-block
                    card (the desktop collage's other cards are dropped here to
                    match the comp). blocks[1] is the "Camping Gear" card.
                -->
                <div class="relative -mx-5 mt-10">
                    <img
                        loading="lazy"
                        :src="asset('images/brand/gifting-center.webp')"
                        alt="Gift Hero in action"
                        class="mx-auto w-full sm:max-w-[520px]"
                    />
                    <div
                        class="relative mx-auto -mt-[20%] max-w-[300px] sm:-mt-[104px]"
                    >
                        <img
                            loading="lazy"
                            :src="asset('images/brand/icon-add.svg')"
                            alt=""
                            aria-hidden="true"
                            class="absolute bottom-full left-1/2 z-20 mb-1 size-[34px] -translate-x-1/2"
                        />
                        <WishBlockCard
                            :title="blocks[1].title"
                            :subtitle="blocks[1].subtitle"
                            :visibility="blocks[1].visibility"
                            :image="blocks[1].image"
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
